// Operaciones · Torniquete · Asistencia
//
// Quién pasó por el torniquete en un rango, a qué hora, y a qué taller venía.
//
// Un límite que la pantalla dice en voz alta en vez de esconder: el equipo NO registra
// salidas identificadas. Se sale con el botón del torniquete y ese evento no lleva
// persona, y el lector reporta las dos puertas como entrada. Así que la hora de salida
// solo aparece si alguna puerta está marcada como "salida" en la pestaña Puertas; si no,
// lo que se muestra es la primera y la última pasada, que es otra cosa.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, Table, Tag, Space, Typography, Row, Col, Button, Alert, DatePicker, Statistic,
} from 'antd';
import { ReloadOutlined, DownloadOutlined, TeamOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { AccesoAdmin } from '../../../../service/Acceso';
import { mensajeDeError } from '../../../../service/client';
import { useNotify } from '../../../../providers/NotificationProvider';
import { exportarHojaUnica } from '../../../../utils/exportExcel';
import useAuthContext from '../../../../hooks/useAuthContext';

const { Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;

const TIPO_PERSONA = {
  inscrito: { color: 'blue', label: 'inscrito' },
  apoderado: { color: 'purple', label: 'apoderado' },
  staff: { color: 'gold', label: 'personal' },
  'sin-vigencia': { color: 'orange', label: 'sin mes al día' },
  desconocido: { color: 'default', label: 'no está en el sistema' },
};

const soloHora = (v) => (v ? String(v).slice(11, 19) : null);

export default function TorniqueteAsistencia() {
  const notify = useNotify();
  const { user } = useAuthContext();

  const [rango, setRango] = useState([dayjs().startOf('day'), dayjs().endOf('day')]);
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);

  const cargar = useCallback(async (desdeHasta) => {
    const [desde, hasta] = desdeHasta || rango;
    if (!desde || !hasta) return;
    setCargando(true);
    try {
      setDatos(await AccesoAdmin.asistencia({
        desde: desde.format('YYYY-MM-DD HH:mm:ss'),
        hasta: hasta.format('YYYY-MM-DD HH:mm:ss'),
      }));
    } catch (e) {
      notify?.error?.(mensajeDeError(e));
    } finally {
      setCargando(false);
    }
  }, [notify, rango]);

  // Carga inicial con el día de hoy. `cargar` queda fuera de las dependencias a
  // propósito: depende de `rango`, así que incluirla volvería a consultar en cada cambio
  // de fecha, y eso ya lo dispara a mano el selector.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { cargar(); }, []);

  const exportar = async () => {
    if (!datos?.personas?.length) return;
    try {
      await exportarHojaUnica({
        archivo: `asistencia-torniquete-${rango[0].format('YYYY-MM-DD')}`,
        titulo: 'Asistencia por torniquete',
        subtitulo: `${rango[0].format('DD-MM-YYYY HH:mm')} a ${rango[1].format('DD-MM-YYYY HH:mm')}`,
        usuario: user,
        filtros: {
          'Rango': `${rango[0].format('DD-MM-YYYY HH:mm')} → ${rango[1].format('DD-MM-YYYY HH:mm')}`,
          'Hora de salida': datos.salidasDisponibles ? 'Disponible' : 'El equipo no registra salidas identificadas',
        },
        nombreHoja: 'Asistencia',
        columnas: [
          { header: 'RUT', key: 'rut', tipo: 'texto', ancho: 14 },
          { header: 'Nombre', key: 'nombre', tipo: 'texto', ancho: 32 },
          { header: 'Tipo', key: 'tipo', tipo: 'texto', ancho: 18 },
          { header: 'Trae a', key: 'trae', tipo: 'texto', ancho: 28 },
          { header: 'Taller', key: 'taller', tipo: 'texto', ancho: 40, ajustarTexto: true },
          { header: 'Bloque', key: 'bloque', tipo: 'texto', ancho: 14 },
          {
            header: 'Mes al día', key: 'alDia', tipo: 'texto', ancho: 11,
            resaltar: (d) => (d.alDia === 'no' ? 'alerta' : d.alDia === 'sí' ? 'ok' : null),
          },
          { header: 'Entrada', key: 'entrada', tipo: 'texto', ancho: 20 },
          { header: 'Primera pasada', key: 'primera', tipo: 'texto', ancho: 20 },
          { header: 'Última pasada', key: 'ultimo', tipo: 'texto', ancho: 20 },
          { header: 'Salida', key: 'salida', tipo: 'texto', ancho: 20 },
          { header: 'Minutos', key: 'minutos', tipo: 'numero', ancho: 9 },
          { header: 'Pasadas', key: 'pasos', tipo: 'numero', ancho: 9 },
          {
            header: 'Rechazos', key: 'rechazos', tipo: 'numero', ancho: 10,
            resaltar: (d) => (d.rechazos > 0 ? 'alerta' : null),
          },
          { header: 'Motivo del rechazo', key: 'motivos', tipo: 'texto', ancho: 30 },
          { header: 'Puertas', key: 'puertas', tipo: 'texto', ancho: 26 },
        ],
        filas: datos.personas.map(p => ({
          rut: p.rut,
          nombre: p.nombre || '',
          tipo: TIPO_PERSONA[p.tipo]?.label || p.tipo,
          trae: p.trae || '',
          taller: p.talleres?.map(t => t.taller).filter(Boolean).join(' / ') || '',
          bloque: p.talleres?.[0]?.bloqueHoy || '',
          alDia: p.alDia === null || p.alDia === undefined ? '' : (p.alDia ? 'sí' : 'no'),
          entrada: p.entradaConocida ? p.entrada : '',
          primera: p.primeraPasada || '',
          ultimo: p.ultimoMovimiento || '',
          salida: p.salidaConocida ? p.salida : '',
          minutos: p.minutosEnRecinto ?? '',
          pasos: p.pasos,
          rechazos: p.rechazos,
          motivos: (p.motivosRechazo || []).join(', '),
          puertas: (p.puertasUsadas || []).join(', '),
        })),
        notas: datos.salidasDisponibles ? [] : [
          'La hora de salida no está disponible: el equipo no registra salidas con identificación.',
          'Para tenerla, marcar una puerta como salida en Operaciones · Torniquete · Puertas.',
        ],
      });
    } catch (e) {
      notify?.error?.(mensajeDeError(e));
    }
  };

  const t = datos?.totales;

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card size="small" title={<span><TeamOutlined /> Asistencia por torniquete</span>}>
        <Space wrap>
          <RangePicker
            showTime={{ format: 'HH:mm' }}
            format="DD-MM-YYYY HH:mm"
            value={rango}
            onChange={(v) => { if (v) { setRango(v); cargar(v); } }}
            allowClear={false}
          />
          <Button icon={<ReloadOutlined />} loading={cargando} onClick={() => cargar()}>
            Actualizar
          </Button>
          <Button
            icon={<DownloadOutlined />}
            disabled={!datos?.personas?.length}
            onClick={exportar}
          >
            Exportar Excel
          </Button>
        </Space>

        {datos && !datos.salidasDisponibles && (
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 12 }}
            message="Todavía no se puede saber a qué hora sale la gente"
            description={
              <span>
                El equipo no registra salidas con identificación: se sale con el botón del
                torniquete y ese evento no lleva persona. Para tener hora de salida hay que
                marcar alguna puerta como <b>salida</b> en la pestaña Puertas; ahí una
                lectura de tarjeta en esa puerta se cuenta como salida. Mientras tanto, lo
                que se muestra es la primera y la última pasada.
              </span>
            }
          />
        )}

        {t && (
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={12} md={4}><Statistic title="Personas" value={t.personas} groupSeparator="." /></Col>
            <Col xs={12} md={4}><Statistic title="Entraron" value={t.conPaso} groupSeparator="." /></Col>
            <Col xs={12} md={5}>
              <Statistic
                title="Solo rechazadas" value={t.soloRechazados} groupSeparator="."
                valueStyle={{ color: t.soloRechazados ? '#d46b08' : undefined }}
              />
            </Col>
            <Col xs={12} md={5}>
              <Statistic
                title="No están en el sistema" value={t.desconocidos} groupSeparator="."
                valueStyle={{ color: t.desconocidos ? '#cf1322' : undefined }}
              />
            </Col>
            <Col xs={12} md={6}><Statistic title="Eventos del equipo" value={t.eventos} groupSeparator="." /></Col>
          </Row>
        )}
      </Card>

      <Card size="small" title="Detalle por persona">
        <Table
          size="small"
          rowKey={(r) => r.rut}
          dataSource={datos?.personas || []}
          loading={cargando}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          locale={{ emptyText: 'Sin movimientos en el rango' }}
          columns={[
            {
              title: 'RUT',
              dataIndex: 'rut',
              width: 108,
              render: (v) => <Text code>{v}</Text>,
            },
            {
              title: 'Persona',
              render: (_, r) => (
                <Space direction="vertical" size={0}>
                  <Text>{r.nombre || <Text type="secondary">sin nombre</Text>}</Text>
                  <Space size={4} wrap>
                    <Tag color={TIPO_PERSONA[r.tipo]?.color}>{TIPO_PERSONA[r.tipo]?.label || r.tipo}</Tag>
                    {r.alDia === false && <Tag color="red">mes vencido</Tag>}
                    {r.trae && <Text type="secondary" style={{ fontSize: 11 }}>trae a {r.trae}</Text>}
                  </Space>
                </Space>
              ),
            },
            {
              title: 'Taller',
              render: (_, r) => (
                <Space direction="vertical" size={0}>
                  {(r.talleres || []).slice(0, 2).map((x, i) => (
                    <Text key={i} style={{ fontSize: 12 }}>{x.taller}{x.bloqueHoy ? ` · ${x.bloqueHoy}` : ''}</Text>
                  ))}
                  {!r.talleres?.length && <Text type="secondary">—</Text>}
                </Space>
              ),
            },
            {
              // Si la persona solo marcó en la puerta de salida no se sabe cuándo entró:
              // se muestra la primera pasada y se dice que es eso.
              title: 'Entrada',
              width: 110,
              render: (_, r) => {
                if (r.entradaConocida) return soloHora(r.entrada);
                if (!r.primeraPasada) return <Text type="secondary">—</Text>;
                return (
                  <Space direction="vertical" size={0}>
                    <Text type="secondary">{soloHora(r.primeraPasada)}</Text>
                    <Text type="secondary" style={{ fontSize: 11 }}>primera pasada</Text>
                  </Space>
                );
              },
            },
            {
              title: 'Salida',
              width: 110,
              render: (_, r) => (r.salidaConocida
                ? <Space direction="vertical" size={0}>
                    <Text>{soloHora(r.salida)}</Text>
                    {r.minutosEnRecinto != null && (
                      <Text type="secondary" style={{ fontSize: 11 }}>{r.minutosEnRecinto} min</Text>
                    )}
                  </Space>
                : <Space direction="vertical" size={0}>
                    <Text type="secondary">{soloHora(r.ultimoMovimiento) || '—'}</Text>
                    <Text type="secondary" style={{ fontSize: 11 }}>última pasada</Text>
                  </Space>
              ),
            },
            {
              title: 'Pasadas',
              width: 82,
              render: (_, r) => <Tag color={r.pasos ? 'green' : 'default'}>{r.pasos}</Tag>,
            },
            {
              title: 'Rechazos',
              render: (_, r) => (r.rechazos
                ? <Space direction="vertical" size={0}>
                    <Tag color="red">{r.rechazos}</Tag>
                    <Text type="secondary" style={{ fontSize: 11 }}>{(r.motivosRechazo || []).join(', ')}</Text>
                  </Space>
                : <Text type="secondary">—</Text>
              ),
            },
          ]}
        />
        <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>
          El RUT lo emite el carnet al pasarlo por el lector, así que los rechazos también
          quedan identificados: "no está en el sistema" significa que ese RUT no tiene
          ninguna inscripción, ni como participante ni como apoderado.
        </Paragraph>
      </Card>
    </Space>
  );
}
